import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IRootReducerState } from '@/store/reducers';
import { extractCssAndJsContent } from '../utils/content-extractors';
import './HtmlFileSelectorPopup.scss';

interface HtmlFileSelectorPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onFileSelect: (fileName: string) => void;
}

const HtmlFileSelectorPopup: React.FC<HtmlFileSelectorPopupProps> = ({
    isOpen,
    onClose,
    onFileSelect
}) => {
    const [selectedFile, setSelectedFile] = useState<string>('');
    const [availableHtmlFiles, setAvailableHtmlFiles] = useState<string[]>([]);
    
    const tempCode = useSelector((state: IRootReducerState) => state.projectData.tempCode);

    // Extract available HTML files when popup opens
    useEffect(() => {
        if (isOpen && tempCode) {
            try {
                const { htmlFiles } = extractCssAndJsContent(tempCode);
                setAvailableHtmlFiles(htmlFiles);
                
                // Set default selection to index.html or first available file
                const defaultFile = htmlFiles.find(f => f.includes('index')) || htmlFiles[0] || '';
                setSelectedFile(defaultFile);
            } catch (error) {
                console.error('Error extracting HTML files:', error);
                setAvailableHtmlFiles([]);
            }
        }
    }, [isOpen, tempCode]);

    const handleConfirm = () => {
        if (selectedFile) {
            onFileSelect(selectedFile);
            onClose();
        }
    };

    const handleCancel = () => {
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="html-file-selector-overlay">
            <div className="html-file-selector-popup">
                <div className="popup-header">
                    <h3>Select HTML File to Edit</h3>
                    <button className="close-button" onClick={handleCancel}>
                        ×
                    </button>
                </div>
                
                <div className="popup-content">
                    <p>Choose which HTML file you want to edit in the visual editor:</p>
                    
                    <div className="file-list">
                        {availableHtmlFiles.length > 0 ? (
                            availableHtmlFiles.map((fileName) => (
                                <label key={fileName} className="file-option">
                                    <input
                                        type="radio"
                                        name="htmlFile"
                                        value={fileName}
                                        checked={selectedFile === fileName}
                                        onChange={(e) => setSelectedFile(e.target.value)}
                                    />
                                    <span className="file-name">{fileName}</span>
                                </label>
                            ))
                        ) : (
                            <p className="no-files">No HTML files found in the project.</p>
                        )}
                    </div>
                </div>
                
                <div className="popup-footer">
                    <button 
                        className="cancel-button" 
                        onClick={handleCancel}
                    >
                        Cancel
                    </button>
                    <button 
                        className="confirm-button" 
                        onClick={handleConfirm}
                        disabled={!selectedFile}
                    >
                        Start Editing
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HtmlFileSelectorPopup; 